import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { sortAlphabetically } from "../lib/sortUtils";
import { buildBiddingGCs, getBiddingGCsDisplayName } from "../lib/biddingGcUtils";
import { formatCurrency } from "../components/job/utils";
import { contactsToList, listToContacts, emptyContact } from "../lib/jobContacts";

export const useCreateJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEdit = !!jobId;
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generalContractors, setGeneralContractors] = useState([]);
  const [saveStatus, setSaveStatus] = useState("saved"); // "saved", "saving", "unsaved"
  const autoSaveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const hasChanges = useRef(false);

  const [form, setForm] = useState({
    name: "",
    client_name: "",
    street: "",
    apt: "",
    city: "",
    state: "",
    zip_code: "",
    description: "",
    start_date: "",
    end_date: "",
    status: "bidding",
    contract_amount: "",
    general_contractor_id: "",
    contract_number: "",
    project_number: "",
    billing_date: "",
    bid_due_date: "",
    bid_due_time: "",
    bidding_gc_ids: [],
    contact_list: [],
  });

  const [laborEstimate, setLaborEstimate] = useState("");
  const [materialEstimate, setMaterialEstimate] = useState("");
  const [equipmentEstimate, setEquipmentEstimate] = useState("");

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await apiClient.get(`/settings/options`);
        setGeneralContractors(res.data.general_contractors || []);
      } catch {
        console.error("Failed to load options");
      }
    };
    fetchOptions();
  }, [getAuthHeaders]);

  // Load existing job data for edit mode
  useEffect(() => {
    if (!isEdit) return;
    const fetchJob = async () => {
      try {
        setLoading(true);
        const res = await apiClient.get(`/jobs/${jobId}`);
        const job = res.data;
        setForm({
          name: job.name || "",
          client_name: job.client_name || "",
          street: job.street || "",
          apt: job.apt || "",
          city: job.city || "",
          state: job.state || "",
          zip_code: job.zip_code || "",
          description: job.description || "",
          start_date: job.start_date || "",
          end_date: job.end_date || "",
          status: job.status || "bidding",
          contract_amount: job.contract_amount || "",
          general_contractor_id: job.general_contractor_id || "",
          contract_number: job.contract_number || "",
          project_number: job.project_number || "",
          billing_date: job.billing_date || "",
          bid_due_date: job.bid_due_date || "",
          bid_due_time: job.bid_due_time || "",
          bidding_gc_ids: (job.bidding_gcs || []).map((gc) => gc.gc_id),
          bidding_gcs: job.bidding_gcs || [],
          contracted_gc_id: job.contracted_gc_id || "",
          contact_list: contactsToList(job.contacts),
        });
        setLaborEstimate(job.total_labor_estimate || "");
        setMaterialEstimate(job.total_material_estimate || "");
        if (job.total_equipment_estimate) {
          setEquipmentEstimate(String(job.total_equipment_estimate));
        } else if (job.equipment_estimates?.length > 0) {
          const total = job.equipment_estimates.reduce((sum, e) => sum + (e.total || 0), 0);
          setEquipmentEstimate(total > 0 ? String(total) : "");
        }
      } catch {
        toast.error("Failed to load job");
        navigate("/jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [isEdit, jobId, getAuthHeaders, navigate]);

  const totalLaborEstimate = parseFloat(laborEstimate) || 0;
  const totalMaterialEstimate = parseFloat(materialEstimate) || 0;
  const totalEquipmentEstimate = parseFloat(equipmentEstimate) || 0;
  const contractAmount = parseFloat(form.contract_amount) || 0;

  const sortedGCs = sortAlphabetically(generalContractors);

  // Company to prefill on new job contacts: the contracted/selected GC, or the single bidding GC.
  const defaultContactCompany = (() => {
    if (form.general_contractor_id) return generalContractors.find((g) => g.id === form.general_contractor_id)?.name || form.client_name || "";
    const ids = form.bidding_gc_ids || [];
    if (ids.length === 1) return generalContractors.find((g) => g.id === ids[0])?.name || form.client_name || "";
    return form.client_name || "";
  })();

  const getSelectedGcName = () => {
    if (!form.general_contractor_id) return form.client_name;
    const gc = generalContractors.find(g => g.id === form.general_contractor_id);
    return gc ? gc.name : form.client_name;
  };

  // Check if job is contracted (has contract_received_date or non-bidding status other than bid_complete)
  const isContracted = form.status !== "bidding" && form.status !== "bid_complete" && form.status !== "not_bid" && form.status !== "not_won";

  const handleGcChange = (gcId) => {
    const gc = generalContractors.find(g => g.id === gcId);
    if (gc) {
      updateForm({
        general_contractor_id: gcId,
        client_name: gc.name,
      });
    } else {
      updateForm({ general_contractor_id: gcId });
    }
  };

  const handleBiddingGcChange = (gcIds) => {
    const names = gcIds
      .map((id) => generalContractors.find((g) => g.id === id)?.name)
      .filter(Boolean);
    updateForm({
      bidding_gc_ids: gcIds,
      client_name: names.length > 0 ? names[0] : "",
    });
  };

  // Wrapper to track changes for auto-save
  const updateForm = useCallback((updates) => {
    setForm(prev => ({ ...prev, ...updates }));
    if (isEdit) {
      hasChanges.current = true;
      setSaveStatus("unsaved");
    }
  }, [isEdit]);

  const markDirty = useCallback(() => {
    if (isEdit) {
      hasChanges.current = true;
      setSaveStatus("unsaved");
    }
  }, [isEdit]);
  const addContact = useCallback((position = "", company = "") => {
    markDirty();
    setForm((prev) => ({ ...prev, contact_list: [...(prev.contact_list || []), emptyContact(position, company)] }));
  }, [markDirty]);
  const updateContactEntry = useCallback((id, field, value) => {
    markDirty();
    setForm((prev) => ({ ...prev, contact_list: (prev.contact_list || []).map((c) => (c.id === id ? { ...c, [field]: value } : c)) }));
  }, [markDirty]);
  const removeContact = useCallback((id) => {
    markDirty();
    setForm((prev) => ({ ...prev, contact_list: (prev.contact_list || []).filter((c) => c.id !== id) }));
  }, [markDirty]);

  // Auto-save for edit mode (1.5s debounce)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!isEdit || !hasChanges.current) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 1500);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  });

  const handleAutoSave = async () => {
    if (!isEdit || !hasChanges.current) return;
    hasChanges.current = false;
    setSaveStatus("saving");
    try {
      await doSave();
      setSaveStatus("saved");
    } catch {
      setSaveStatus("unsaved");
    }
  };

  const buildPayload = () => {
    const { bidding_gc_ids, bidding_gcs: existingGCs, contracted_gc_id, contact_list, ...formFields } = form;

    const bidding_gcs = buildBiddingGCs(bidding_gc_ids || [], existingGCs || [], generalContractors);

    let clientName = getSelectedGcName();
    if (
      (formFields.status === "bidding" || formFields.status === "bid_complete") &&
      bidding_gcs.length > 0
    ) {
      clientName = getBiddingGCsDisplayName(bidding_gcs);
    }

    return {
      ...formFields,
      client_name: clientName,
      contract_amount: contractAmount,
      bidding_gcs,
      contacts: listToContacts(contact_list),
      labor_estimates: totalLaborEstimate > 0 ? [{
        employee_id: "lump_sum",
        employee_name: "Labor Estimate (Lump Sum)",
        hours: 0,
        hourly_rate: 0,
        total: totalLaborEstimate,
      }] : [],
      material_estimates: totalMaterialEstimate > 0 ? [{
        material_id: "lump_sum",
        material_name: "Material Estimate (Lump Sum)",
        quantity: 1,
        unit_price: totalMaterialEstimate,
        total: totalMaterialEstimate,
      }] : [],
      equipment_estimates: totalEquipmentEstimate > 0 ? [{
        equipment_id: "lump_sum",
        equipment_name: "Equipment Estimate (Lump Sum)",
        quantity: 1,
        unit: "lump_sum",
        unit_price: totalEquipmentEstimate,
        total: totalEquipmentEstimate,
      }] : [],
      total_equipment_estimate: totalEquipmentEstimate,
    };
  };

  const doSave = async () => {
    const payload = buildPayload();
    if (isEdit) {
      await apiClient.put(`/jobs/${jobId}`, payload);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = buildPayload();

      if (isEdit) {
        await apiClient.put(`/jobs/${jobId}`, payload);
        toast.success("Job updated successfully");
        navigate(`/jobs/${jobId}`);
      } else {
        // New jobs always start in "bidding" status
        payload.status = "bidding";
        const response = await apiClient.post(`/jobs`, payload);
        toast.success("Job created successfully");
        navigate(`/jobs/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} job`);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading, generalContractors, isEdit, isContracted,
    form, laborEstimate, materialEstimate, equipmentEstimate,
    totalLaborEstimate, totalMaterialEstimate, totalEquipmentEstimate,
    contractAmount, sortedGCs, saveStatus, defaultContactCompany,
    setForm: updateForm, setLaborEstimate, setMaterialEstimate, setEquipmentEstimate,
    addContact,
    updateContactEntry,
    removeContact,
    handleGcChange, handleBiddingGcChange, handleSubmit,
    navigate, formatCurrency,
  };
};
